import keycloak from '@auth/keycloak';
import { KeycloakUserInfo } from '@auth/models/UserInfo.model';
import { AuthActions } from '@shared/store/auth/auth';
import { KeycloakInitOptions, KeycloakLoginOptions } from 'keycloak-js';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

// Keycloak initialization options for web browsers
const keycloakProviderInitOptions: KeycloakInitOptions = {
  onLoad: 'login-required', // Auto-redirect to login if not authenticated
  checkLoginIframe: false // Disable iframe-based session checking
};

// Web browser implementation of Keycloak authentication
export const useKeycloakAuthWeb = () => {
  const dispatch = useDispatch();

  // Store original login method for impersonation support
  const originalLogin = useCallback(() => {
    return keycloak.login.bind(keycloak);
  }, [])();

  // Standard login flow for web browsers
  const login = useCallback(async () => {
    await keycloak.createLoginUrl();
    keycloak.login();
  }, []);

  // Handle successful authentication by loading user info and updating Redux state
  const onAuthSuccess = useCallback(async () => {
    const userInfo = await keycloak.loadUserInfo();
    dispatch(
      AuthActions.loginSuccess({
        isAuthenticated: true,
        user: userInfo as KeycloakUserInfo,
        userType: (userInfo as KeycloakUserInfo).efka_access.role,
        tokenParsed: keycloak.tokenParsed
      })
    );
  }, [dispatch]);

  // Handle token expiration by attempting refresh or forcing re-login
  const onTokenExpired = useCallback(() => {
    keycloak
      .updateToken(30) // Refresh if token will expire in 30 seconds
      .catch((error) => {
        console.error('Failed to refresh token', error);
        login();
      });
  }, [login]);

  // Enhanced login with impersonation support (web-only feature)
  const keycloakLogin = useCallback(
    (options: KeycloakLoginOptions) => {
      const urlParams = new URLSearchParams(window.location.search);
      // NOTE: impersonation logic is ONLY applicable in web context
      const shouldImpersonate = urlParams.get('impersonate') === 'true';

      if (shouldImpersonate) {
        // Use internal Keycloak identity provider for impersonation
        return originalLogin({ ...options, idpHint: 'internal-keycloak' });
      } else {
        return originalLogin(options);
      }
    },
    [originalLogin]
  );

  // Web logout: clear Redux state and redirect to Keycloak logout
  const logout = useCallback(async () => {
    dispatch(AuthActions.logout());
    keycloak.logout({ redirectUri: window.location.origin });
  }, [dispatch]);

  // Initialize Keycloak on component mount
  useEffect(() => {
    if (keycloak.authenticated !== undefined) {
      // Already initialized
      return;
    }

    console.log('Initializing Keycloak...');

    // Set up event handlers
    keycloak.onTokenExpired = onTokenExpired;
    keycloak.onAuthSuccess = onAuthSuccess;
    keycloak.onAuthLogout = logout;
    keycloak.login = keycloakLogin; // Override with impersonation-aware login

    // Initialize Keycloak and handle authentication state
    keycloak
      .init(keycloakProviderInitOptions)
      .then((authenticated) => {
        if (!authenticated) {
          login();
        }
      })
      .catch((error) => {
        console.error('Keycloak authentication failed', error);
        login();
      });
  }, [dispatch, onTokenExpired, onAuthSuccess, logout, keycloakLogin, login]);

  return { logout, login };
};
